import { Injectable, inject } from '@angular/core';
import { PostgrestError } from '@supabase/supabase-js';
import { CorrectionWithUser } from '../models/correction.model';
import { CreateDiscountCouponInput, DiscountCoupon } from '../models/coupon.model';
import { SystemSettings, LogEntry } from '../models/settings.model';
import { Plan, SubscriptionWithDetails } from '../models/subscription.model';
import { User } from '../models/user.model';
import { AuthService } from './auth.service';
import { LogService } from './log.service';
import { SupabaseService } from './supabase.service';

export interface AdminStatistics {
  totalUsers: number;
  totalCorrections: number;
  averageScore: number;
  activeUsersLast30Days: number;
}

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  private supabase = inject(SupabaseService).client;
  private authService = inject(AuthService);
  private logService = inject(LogService);
  private missingRpcWarnings = new Set<string>();

  private handleError(error: PostgrestError | Error | null, context: string) {
    if (error) {
      void this.logService.logError(`AdminService:${context}`, error, { context });
    } else {
      void this.logService.logError(`AdminService:${context}`, undefined, {
        context,
        detail: 'Unknown error (null/undefined)',
      });
    }
  }

  private handleRpcError(error: PostgrestError | null, functionName: string): boolean {
    if (error?.message.includes(functionName) && (error.message.includes('does not exist') || error.message.includes('Could not find'))) {
      if (!this.missingRpcWarnings.has(functionName)) {
        this.missingRpcWarnings.add(functionName);
        void this.logService.logWarn('AdminService RPC missing or inaccessible', {
          functionName,
          errorMessage: error.message,
          hint: 'Execute/verify SQL bootstrap for required admin RPC functions',
        });
      }
      return true;
    }

    return false;
  }

  private isDemoMode(): boolean {
    const user = this.authService.currentUser();
    return !!user && user.id.startsWith('mock-user-id');
  }

  private async guardPrivilegedWrite(actionLabel: string): Promise<void> {
    await this.authService.ensureAdminPrivilegedSession(actionLabel);
  }

  async getUsers(): Promise<User[]> {
    if (this.isDemoMode()) return [];
    const { data, error } = await this.supabase.rpc('get_all_users');
    if (error) {
      this.handleRpcError(error, 'get_all_users');
      this.handleError(error, 'getUsers');
      return [];
    }
    if (!data) return [];
    return data.map((profile: any) => ({
      id: profile.id,
      name: profile.name ?? '',
      email: profile.email ?? '',
      credits: Number(profile.credits ?? 0),
      plan: profile.plan ?? 'gratuito',
      essaysSubmitted: 0,
      createdAt: profile.created_at ? new Date(profile.created_at) : new Date(),
      isAdmin: Boolean(profile.isAdmin ?? profile.isadmin ?? false),
      last_free_correction_at: profile.last_free_correction_at ?? '',
      has_used_pro_trial: Boolean(profile.has_used_pro_trial),
    })) as User[];
  }

  async getUserById(id: string): Promise<User | null> {
    if (this.isDemoMode()) return null;
    const { data, error } = await this.supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .limit(1)
      .single();

    if (error) this.handleError(error, 'getUserById');
    return data as User | null;
  }

  async addUser(user: Pick<User, 'name' | 'email' | 'credits' | 'plan'>): Promise<any> {
    if (this.isDemoMode()) return null;
    await this.guardPrivilegedWrite('criar usuário');
    void this.logService.logWarn('AdminService:addUser called without auth bootstrap', {
      email: user.email,
      plan: user.plan,
      detail: 'Creating profile directly requires matching auth user',
    });
    const { data, error } = await this.supabase
      .from('profiles')
      .insert([
        { name: user.name, email: user.email, credits: user.credits, plan: user.plan }
      ])
      .select()
      .single();

    if (error) {
      this.handleError(error, 'addUser');
      throw new Error(`Não foi possível criar usuário: ${error.message}`);
    }
    return data;
  }

  async updateUser(updatedUser: User): Promise<User | null> {
    if (this.isDemoMode()) return updatedUser;
    await this.guardPrivilegedWrite('atualizar usuário');
    const { data, error } = await this.supabase
      .rpc('update_user_as_admin', {
        p_args: {
          user_id: updatedUser.id,
          name: updatedUser.name,
          email: updatedUser.email,
          credits: updatedUser.credits,
          plan: updatedUser.plan,
        }
      })
      .select()
      .single();

    if (error) {
      this.handleError(error, 'updateUser');
      throw new Error(`Não foi possível atualizar usuário: ${error.message}`);
    }
    if (!data) {
      throw new Error('Não foi possível atualizar usuário: resposta vazia do servidor.');
    }
    void this.logService.logInfo('Admin updated user profile', {
      domain: 'admin',
      event: 'admin.user.updated',
      status: 'success',
      targetUserId: updatedUser.id,
      plan: updatedUser.plan,
      credits: updatedUser.credits,
    });
    return data as User | null;
  }

  async adjustUserCredits(userId: string, delta: number): Promise<User | null> {
    if (this.isDemoMode()) return null;
    await this.guardPrivilegedWrite('ajustar créditos');

    const normalizedDelta = Math.trunc(Number(delta));
    if (!Number.isFinite(normalizedDelta) || normalizedDelta === 0) {
      throw new Error('Informe um ajuste de créditos diferente de zero.');
    }

    const { data: currentUser, error: currentUserError } = await this.supabase
      .from('profiles')
      .select('id, name, email, credits, plan, "isAdmin", last_free_correction_at, has_used_pro_trial')
      .eq('id', userId)
      .single();

    if (currentUserError || !currentUser) {
      this.handleError(currentUserError, 'adjustUserCredits(loadUser)');
      throw new Error('Não foi possível carregar o usuário para ajuste de créditos.');
    }

    const nextCredits = Math.max(0, Number(currentUser.credits ?? 0) + normalizedDelta);

    const updated = await this.updateUser({
      id: currentUser.id,
      name: currentUser.name ?? '',
      email: currentUser.email ?? '',
      credits: nextCredits,
      plan: currentUser.plan ?? 'gratuito',
      essaysSubmitted: 0,
      createdAt: new Date(),
      isAdmin: Boolean((currentUser as any).isAdmin),
      last_free_correction_at: currentUser.last_free_correction_at ?? '',
      has_used_pro_trial: Boolean(currentUser.has_used_pro_trial),
    });
    void this.logService.logInfo('Admin adjusted user credits', {
      domain: 'admin',
      event: 'admin.user.credits_adjusted',
      status: 'success',
      targetUserId: userId,
      delta: normalizedDelta,
      previousCredits: Number(currentUser.credits ?? 0),
      nextCredits,
    });

    return updated;
  }

  async deleteUser(userId: string): Promise<void> {
    if (this.isDemoMode()) return;
    await this.guardPrivilegedWrite('remover usuário');
    const { error } = await this.supabase
      .from('profiles')
      .delete()
      .eq('id', userId);

    if (error) {
      this.handleError(error, 'deleteUser');
      throw new Error(`Não foi possível remover usuário: ${error.message}`);
    }
  }

  async getStatistics(): Promise<AdminStatistics> {
    const defaultStats: AdminStatistics = { totalUsers: 0, totalCorrections: 0, averageScore: 0, activeUsersLast30Days: 0 };
    if (this.isDemoMode()) return defaultStats;

    const [userCountResult, correctionsCountResult, averageScoreResult] = await Promise.all([
      this.supabase.rpc('get_users_count'),
      this.supabase.rpc('get_corrections_count'),
      this.supabase.rpc('get_average_score')
    ]);

    if (userCountResult.error) this.handleRpcError(userCountResult.error, 'get_users_count');
    if (correctionsCountResult.error) this.handleRpcError(correctionsCountResult.error, 'get_corrections_count');
    if (averageScoreResult.error) this.handleRpcError(averageScoreResult.error, 'get_average_score');

    if (userCountResult.error || correctionsCountResult.error || averageScoreResult.error) {
      return defaultStats;
    }

    return {
      totalUsers: userCountResult.data ?? 0,
      totalCorrections: correctionsCountResult.data ?? 0,
      averageScore: averageScoreResult.data ?? 0,
      activeUsersLast30Days: 0
    };
  }

  async getCorrections(): Promise<CorrectionWithUser[]> {
    if (this.isDemoMode()) return [];
    const { data, error } = await this.supabase.rpc('get_all_corrections_with_details');
    if (error) {
      this.handleRpcError(error, 'get_all_corrections_with_details');
      this.handleError(error, 'getCorrections');
      return [];
    }
    return (data as CorrectionWithUser[]) || [];
  }

  async getPlans(): Promise<Plan[]> {
    if (this.isDemoMode()) return [];
    const { data, error } = await this.supabase.rpc('get_all_plans');
    if (error) {
      this.handleRpcError(error, 'get_all_plans');
      this.handleError(error, 'getPlans');
      return [];
    }
    return (data as Plan[]) || [];
  }

  async getSubscriptions(): Promise<SubscriptionWithDetails[]> {
    if (this.isDemoMode()) return [];
    const { data, error } = await this.supabase.rpc('get_all_subscriptions_with_details');
    if (error) {
      this.handleRpcError(error, 'get_all_subscriptions_with_details');
      this.handleError(error, 'getSubscriptions');
      return [];
    }
    return ((data as any[]) || []).map((subscription) => ({
      ...subscription,
      mercadopago_payment_id: subscription.mercadopago_payment_id ?? subscription.provider_subscription_id ?? '',
    })) as SubscriptionWithDetails[];
  }

  async getCoupons(): Promise<DiscountCoupon[]> {
    if (this.isDemoMode()) return [];

    const { data, error } = await this.supabase
      .from('discount_coupons')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      this.handleError(error, 'getCoupons');
      return [];
    }

    return (data ?? []) as DiscountCoupon[];
  }

  async createCoupon(input: CreateDiscountCouponInput): Promise<DiscountCoupon | null> {
    if (this.isDemoMode()) return null;
    await this.guardPrivilegedWrite('criar cupom');

    const normalizedCode = String(input.code ?? '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9_-]/g, '')
      .slice(0, 40);

    if (!normalizedCode) {
      throw new Error('Codigo de cupom invalido.');
    }

    const discountValue = Math.max(1, Math.trunc(Number(input.discount_value ?? 0)));
    const maxUses = input.max_uses === null ? null : Math.max(1, Math.trunc(Number(input.max_uses ?? 0)));

    const { data, error } = await this.supabase
      .from('discount_coupons')
      .insert({
        code: normalizedCode,
        description: String(input.description ?? '').trim(),
        discount_type: input.discount_type,
        discount_value: discountValue,
        max_uses: maxUses,
        starts_at: input.starts_at,
        ends_at: input.ends_at,
      })
      .select()
      .single();

    if (error) {
      this.handleError(error, 'createCoupon');
      throw new Error(`Não foi possível criar cupom: ${error.message}`);
    }

    return data as DiscountCoupon;
  }

  async updateCoupon(couponId: number, patch: Partial<CreateDiscountCouponInput> & { is_active?: boolean }): Promise<DiscountCoupon | null> {
    if (this.isDemoMode()) return null;
    await this.guardPrivilegedWrite('atualizar cupom');

    const payload: Record<string, unknown> = {};
    if (patch.code !== undefined) {
      payload.code = String(patch.code ?? '')
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9_-]/g, '')
        .slice(0, 40);
    }
    if (patch.description !== undefined) payload.description = String(patch.description ?? '').trim();
    if (patch.discount_type !== undefined) payload.discount_type = patch.discount_type;
    if (patch.discount_value !== undefined) payload.discount_value = Math.max(1, Math.trunc(Number(patch.discount_value ?? 0)));
    if (patch.max_uses !== undefined) {
      payload.max_uses = patch.max_uses === null ? null : Math.max(1, Math.trunc(Number(patch.max_uses ?? 0)));
    }
    if (patch.starts_at !== undefined) payload.starts_at = patch.starts_at;
    if (patch.ends_at !== undefined) payload.ends_at = patch.ends_at;
    if (patch.is_active !== undefined) payload.is_active = Boolean(patch.is_active);

    const { data, error } = await this.supabase
      .from('discount_coupons')
      .update(payload)
      .eq('id', couponId)
      .select()
      .single();

    if (error) {
      this.handleError(error, 'updateCoupon');
      throw new Error(`Não foi possível atualizar cupom: ${error.message}`);
    }

    return data as DiscountCoupon;
  }

  async createPlan(plan: Omit<Plan, 'id' | 'created_at'>): Promise<Plan | null> {
    if (this.isDemoMode()) return null;
    await this.guardPrivilegedWrite('criar plano');
    const { data, error } = await this.supabase
      .rpc('create_plan', {
        p_name: plan.name,
        p_description: plan.description,
        p_price: plan.price,
        p_interval: plan.interval,
        p_credits_granted: plan.credits_granted,
      })
      .select()
      .single();

    if (error) this.handleError(error, 'createPlan');
    return data as Plan | null;
  }

  async updatePlan(plan: Plan): Promise<Plan | null> {
    if (this.isDemoMode()) return plan;
    await this.guardPrivilegedWrite('atualizar plano');
    const { data, error } = await this.supabase
      .rpc('update_plan', {
        p_plan_id: plan.id,
        p_name: plan.name,
        p_description: plan.description,
        p_price: plan.price,
        p_interval: plan.interval,
        p_credits_granted: plan.credits_granted,
      })
      .select()
      .single();

    if (error) this.handleError(error, 'updatePlan');
    return data as Plan | null;
  }

  async getSettings(): Promise<SystemSettings> {
    const defaults: SystemSettings = {
      integrations: { siteUrl: '', supportEmail: 'contato@usecorrectify.com' },
      content: { termsOfUse: '', privacyPolicy: '', defaultWelcomeMessage: '' },
    };
    if (this.isDemoMode()) return defaults;

    const { data, error } = await this.supabase.rpc('get_system_settings');
    if (error) {
      this.handleRpcError(error, 'get_system_settings');
      this.handleError(error, 'getSettings');
      return defaults;
    }

    const dbSettings: { [key: string]: any } = {};
    if (data) {
      for (const row of (data as any[])) {
        dbSettings[row.key] = row.value;
      }
    }

    return {
      integrations: { ...defaults.integrations, ...dbSettings['integrations'] },
      content: { ...defaults.content, ...dbSettings['content'] }
    };
  }

  async saveSettings(settings: SystemSettings): Promise<void> {
    if (this.isDemoMode()) return;
    await this.guardPrivilegedWrite('salvar configurações');
    const { error: integrationsError } = await this.supabase.rpc('update_system_setting', {
      p_key: 'integrations',
      p_value: settings.integrations
    });
    if (integrationsError) this.handleError(integrationsError, 'saveSettings (integrations)');

    const { error: contentError } = await this.supabase.rpc('update_system_setting', {
      p_key: 'content',
      p_value: settings.content
    });
    if (contentError) this.handleError(contentError, 'saveSettings (content)');

    if (!integrationsError && !contentError) {
      void this.logService.logInfo('Admin saved system settings', {
        domain: 'admin',
        event: 'admin.settings.saved',
        status: 'success',
      });
    }
  }

  async getSystemLogs(): Promise<LogEntry[]> {
    if (this.isDemoMode()) return [];
    const { data, error } = await this.supabase.rpc('get_system_logs');
    if (error) {
      this.handleRpcError(error, 'get_system_logs');
      this.handleError(error, 'getSystemLogs');
      return [];
    }
    return (data || []).map((log: any) => ({
      ...log,
      created_at: new Date(log.created_at),
    })) as LogEntry[];
  }

  async getReportedCorrections(): Promise<CorrectionWithUser[]> {
    if (this.isDemoMode()) return [];
    const { data, error } = await this.supabase.rpc('get_reported_corrections');
    if (error) {
      this.handleRpcError(error, 'get_reported_corrections');
      this.handleError(error, 'getReportedCorrections');
      return [];
    }
    return (data as CorrectionWithUser[]) || [];
  }

  async resolveCorrectionReport(correctionId: number, newStatus: 'reviewed_ok' | 'reviewed_removed'): Promise<void> {
    if (this.isDemoMode()) return;
    await this.guardPrivilegedWrite('moderar redação');
    const { error } = await this.supabase
      .rpc('resolve_correction_report', {
        p_correction_id: correctionId,
        p_new_status: newStatus
      });

    if (error) this.handleError(error, 'resolveCorrectionReport');
  }
}
